import { useParams } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { cn } from '@/lib/utils/helpers'
import type { ChargebackDetailChargebackTab } from '../types/pages'
import { useChargebackDetailPage } from '../hooks/useChargebackDetailPage'
import { ChargebackConfirmModal } from '../components/chargeback-detail/ChargebackConfirmModal'
import { ChargebackDetailSummary } from '../components/chargeback-detail/ChargebackDetailSummary'
import { ChargebackInvestigationTab } from '../components/chargeback-detail/ChargebackInvestigationTab'
import { ChargebackDecisionTab } from '../components/chargeback-detail/ChargebackDecisionTab'
import { ChargebackHistoryTab } from '../components/chargeback-detail/ChargebackHistoryTab'
import { CHARGEBACK_TABS } from '../constants/chargebackDetailConstants'

export function ChargebackDetailPage() {
  const { applicantId = '', logId = '' } = useParams<{
    applicantId: string
    logId: string
  }>()
  const page = useChargebackDetailPage(applicantId, logId)

  return (
    <div data-testid="chargeback-detail-page">
      <PageShell
        title="Chargeback Details"
        pageHeaderTestIdRoot="chargeback-detail-page-header"
        contentClassName="!space-y-5 w-full max-w-none min-w-0 px-4 sm:px-6 lg:px-8 xl:px-10"
      >
        {page.loading ? (
          <div className="flex min-h-[18rem] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#1597ff]" />
          </div>
        ) : (
          <>
            <ChargebackDetailSummary applicantId={applicantId} detail={page.detail} />

            <div
              className="grid grid-cols-3 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm divide-x divide-slate-100"
              data-testid="chargeback-detail-tabs"
              role="tablist"
              aria-label="Chargeback detail tabs"
            >
              {CHARGEBACK_TABS.map((tab) => {
                const Icon = tab.icon
                const isActive = page.activeTab === tab.id
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    data-testid={tab.testId}
                    aria-selected={isActive}
                    onClick={() => page.setActiveTab(tab.id as ChargebackDetailChargebackTab)}
                    className={cn(
                      'relative flex h-12 items-center justify-center gap-2.5 px-4 text-sm transition-colors select-none',
                      isActive
                        ? 'bg-[#f0f7ff] font-bold text-[#1597ff] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2.5px] after:bg-[#1597ff]'
                        : 'bg-white font-semibold text-slate-600 hover:bg-slate-50/70 hover:text-slate-900',
                    )}
                  >
                    <Icon
                      className={cn('h-4 w-4 shrink-0', isActive ? 'text-[#1597ff]' : 'text-slate-400')}
                      aria-hidden
                    />
                    <span>{tab.label}</span>
                  </button>
                )
              })}
            </div>

            {page.activeTab === 'investigation' ? (
              <ChargebackInvestigationTab applicantId={applicantId} page={page} />
            ) : null}

            {page.activeTab === 'decision' ? (
              <ChargebackDecisionTab applicantId={applicantId} page={page} />
            ) : null}

            {page.activeTab === 'history' ? <ChargebackHistoryTab page={page} /> : null}
          </>
        )}
      </PageShell>

      {page.packetConfirmOpen ? (
        <ChargebackConfirmModal
          title="Chargeback Packet"
          message="Do you want to generate Chargeback Packet?"
          primaryLabel="Generate"
          onPrimary={() => void page.handleGeneratePacket()}
          onCancel={() => page.setPacketConfirmOpen(false)}
          panelDataTestId="chargeback-packet-confirm-modal"
        />
      ) : null}

      {page.deleteFileId != null ? (
        <ChargebackConfirmModal
          title="Remove File?"
          message="Are you sure you want to remove the file?"
          primaryLabel="Remove"
          onPrimary={() => void page.confirmDeleteFile()}
          onCancel={() => page.setDeleteFileId(null)}
          panelDataTestId="chargeback-remove-file-confirm-modal"
        />
      ) : null}
    </div>
  )
}
